import {
  AbilityBuilder,
  Ability,
  AbilityClass,
  MongoQuery,
  defineAbility,
} from '@casl/ability';
import { PermissionAction, PermissionObject } from '@jaam/commons/constants';

export declare type AnyObject = Record<PropertyKey, unknown>;

export type PermissionObjectType = MongoQuery<AnyObject>;

export type Actions = `${PermissionAction}`;

export type Subjects = `${PermissionObject}`;

export type AppAbility = Ability<[Actions, Subjects]>;
export const AppAbility = Ability as AbilityClass<AppAbility>;
export interface PermissionCondition {
  [key: string]: any;
}

export function ParseCondition(
  condition: PermissionCondition,
  variables: Record<string, any>,
): any {
  if (!condition) return null;
  const parsedCondition = {} as PermissionCondition;
  for (const [key, rawValue] of Object.entries(condition)) {
    if (rawValue !== null && typeof rawValue === 'object') {
      if (key.includes('$')) {
        parsedCondition[key] = rawValue;
        continue;
      } else {
        const value = ParseCondition(rawValue, variables);
        parsedCondition[key] = value;
        continue;
      }
    }
    if (typeof rawValue !== 'string') {
      parsedCondition[key] = rawValue;
      continue;
    }
    // find placeholder "{}""
    const matches = /{([a-zA-Z0-9]+)}$/.exec(rawValue);
    if (!matches) {
      parsedCondition[key] = rawValue;
      continue;
    }

    const value = variables[matches[1]];
    if (typeof value === 'undefined') {
      throw new ReferenceError(`Variable ${value} is not defined`);
    }
    parsedCondition[key] = value;
  }
  return parsedCondition;
}

export function defineRulesFor(
  data: Array<{ action: Actions; subject: Subjects; conditions?: any }>,
  userId: number,
) {
  const { can, rules } = new AbilityBuilder(AppAbility);
  if (data) {
    data.forEach((p) => {
      can(
        p.action,
        p.subject,
        ParseCondition(p.conditions, {
          id: userId,
        }),
      );
    });
  }

  return rules;
}

export function buildAbilityFor(
  data: Array<{ action: Actions; subject: Subjects }>,
  userId: number,
): AppAbility {
  return new AppAbility(defineRulesFor(data, userId), {
    // https://casl.js.org/v5/en/guide/subject-type-detection
    detectSubjectType: (object: any) => object?.type,
  });
}

export function checkAbility(
  data: Array<{ action: Actions; subject: Subjects; conditions?: any }>,
  userId: number,
) {
  return defineAbility(
    (can) => {
      if (data) {
        data.forEach((p) => {
          can(
            p.action,
            p.subject,
            ParseCondition(p.conditions, {
              id: userId,
            }),
          );
        });
      }
    },
    {
      detectSubjectType: (object: any) => object?.type,
    },
  );
}

export const ability = new Ability<[Actions, Subjects], PermissionObjectType>(
  [],
);
